import { PlusOutlined, SyncOutlined } from '@ant-design/icons';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';
import { KlaviyoIcon } from '@mm/icons';
import { useTagsCRUDActions } from '@mm/state/actions/TagsCRUDActions';
import ConnectionModel, {
  KlaviyoAccessToken,
  KlaviyoOauthToken,
} from '@mm/state/model/ConnectionModel';
import { selectAudienceSorted } from '@mm/state/selectors/messengerAdSelectors';
import { tagsOfKinds } from '@mm/state/selectors/tags';
import { StoreState } from '@mm/state/store-interfaces';
import { Switch } from 'antd';
import { ReactComponent as CustomersAiLogo } from 'assets/icons/customersai-logo.svg';
import classNames from 'classnames';
import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { ContactsSync } from '../ContactsSync/ContactsSync';
import { KlaviyoConnectionEditor } from './KlaviyoConnectionEditor';
import { KlaviyoErrorMessage } from './KlaviyoErrorMessage';
import styles from './KlaviyoIntegrations.module.css';
import { useManageConnection } from './use-manage-connection';

type Props = {
  klaviyoConnections: ConnectionModel[];
  account: KlaviyoOauthToken | KlaviyoAccessToken;
  activeFbPageId: number;
  connectionsMessage: string | null;
  getLists: (
    pageId: number,
    accountId: number,
    tokenType: 'api_key' | 'oauth_token',
    force: boolean,
  ) => void;
  controlsDisabled: boolean;
  tokenType: 'api_key' | 'oauth_token';
};

export const KlaviyoSyncSetup = (props: Props) => {
  const {
    klaviyoConnections,
    account,
    activeFbPageId,
    getLists,
    controlsDisabled,
    connectionsMessage,
    tokenType,
  } = props;
  const availableTags = useSelector((state: StoreState) =>
    tagsOfKinds(state, ['regular']),
  );
  const audiences = useSelector(selectAudienceSorted);
  const klaviyoLists = useSelector((state: StoreState) => state.klaviyo.lists);
  const lists = (klaviyoLists?.length ? klaviyoLists : []).map(list => ({
    id: list.id,
    name: list.attributes.name,
  }));

  const { companyName = 'Customers.ai', isWhitelabelCompany } =
    useWhitelabelConfig();

  const { getAll } = useTagsCRUDActions();

  const connectionTemplate = useCallback(
    (extra: Partial<ConnectionModel> = {}): Partial<ConnectionModel> => {
      const key = tokenType === 'api_key' ? 'accessTokenId' : 'oauthTokenId';
      const params = { [key]: account.id };
      return {
        active: true,
        name: 'Klaviyo Connection',
        connectionKind: 'klaviyo',
        klaviyoParams: params,
        ...extra,
      };
    },
    [account, tokenType],
  );

  const validConnection = (
    isAllContactsSync: boolean,
    connection: Partial<ConnectionModel>,
  ) =>
    Boolean(
      (!isAllContactsSync ? connection.audienceId : !connection.audienceId) &&
        connection.data?.listId,
    );

  const {
    allConnections,
    isAllContactsSync,
    selectableLists,
    selectableAudiences,
    addConnection,
    remove,
    updateOrEdit,
    onChangeType,
  } = useManageConnection({
    connections: klaviyoConnections,
    audiences,
    lists,
    validConnection,
    connectionTemplate,
  });

  const {
    data: { skipName },
  } = allConnections[0] as ConnectionModel;
  const updateSkipNames = (ok: boolean) => {
    allConnections.forEach((conn, idx) => {
      updateOrEdit(idx, { data: { ...(conn?.data || {}), skipName: ok } });
    });
  };

  useEffect(() => {
    getAll(activeFbPageId, true);
    getLists(activeFbPageId, account.id, tokenType, false);
  }, []);

  const canAdd = selectableLists()?.length && selectableAudiences().length;
  // all contacts webhook is always there, so 2 connections means 1 all contacts + 1 audience based
  const multipleConnections = allConnections.length > 2;

  return (
    <ContactsSync
      isReadOnly={controlsDisabled}
      allConnections={allConnections as ConnectionModel[]}
      connection={allConnections[0] as ConnectionModel}
      onRadioChange={onChangeType}
      name="Klaviyo"
    >
      <div className={styles.syncSetupControls}>
        <PrimaryButton
          className={classNames(
            'integrations__header-add-button-secondary',
            'integrations__header-add-button-action',
            'integrations__header-button-no-margin',
          )}
          onClick={_ => getLists(activeFbPageId, account.id, tokenType, true)}
          leftIcon={<SyncOutlined />}
        >
          Refresh lists
        </PrimaryButton>
        <div className={styles.namesInclusionSelector}>
          <Switch
            defaultChecked={!skipName}
            size="small"
            className={classNames(styles.switch, styles.nameInclusionSwitch)}
            onChange={ok => updateSkipNames(!ok)}
          />
          Sync first and last name
        </div>
      </div>
      {connectionsMessage && (
        <KlaviyoErrorMessage
          classNames={styles.syncWarningBox}
          error={connectionsMessage}
        />
      )}
      <div className={styles.gridContainer}>
        {!isAllContactsSync ? (
          <>
            <div className={styles.gridAudiences}>
              <div className={classNames(styles.gridColumn, styles.firstRow)}>
                {!isWhitelabelCompany && (
                  <CustomersAiLogo className={styles.gridIcon} />
                )}{' '}
                {companyName}
                Audience
              </div>
              <div className={classNames(styles.gridColumn, styles.firstRow)} />
              <div className={classNames(styles.gridColumn, styles.firstRow)}>
                <div
                  className={classNames(styles.gridIcon, styles.klaviyoIcon)}
                >
                  <KlaviyoIcon size={12} />
                </div>
                Klaviyo List
              </div>
              <div className={classNames(styles.gridColumn, styles.firstRow)}>
                Add tags to contacts in Klaviyo
              </div>
              <div className={classNames(styles.gridColumn, styles.firstRow)}>
                Actions
              </div>
              {allConnections.map((conn, idx) => (
                <KlaviyoConnectionEditor
                  key={`connection-${idx}`}
                  canDelete={multipleConnections}
                  connection={conn as ConnectionModel}
                  withAudiences={true}
                  lists={selectableLists(conn)}
                  audiences={selectableAudiences(conn)}
                  update={props => updateOrEdit(idx, props)}
                  remove={() => remove(idx)}
                  activeFbPageId={activeFbPageId}
                  availableTags={availableTags}
                />
              ))}
            </div>
            {canAdd ? (
              <PrimaryButton
                className={classNames(
                  'integrations__header-add-button-secondary',
                  'integrations__header-add-button-action',
                  'integrations__header-button-no-margin',
                )}
                onClick={addConnection}
                leftIcon={<PlusOutlined />}
              >
                Add
              </PrimaryButton>
            ) : null}
          </>
        ) : (
          <div className={styles.gridAll}>
            <div className={classNames(styles.gridColumn, styles.firstRow)}>
              <div className={classNames(styles.gridIcon, styles.klaviyoIcon)}>
                <KlaviyoIcon size={12} />
              </div>
              Klaviyo List
            </div>
            <div className={classNames(styles.gridColumn, styles.firstRow)}>
              Add tags to contacts in Klaviyo
            </div>
            <div className={classNames(styles.gridColumn, styles.firstRow)}>
              Actions
            </div>
            {allConnections.map((conn, idx) => (
              <KlaviyoConnectionEditor
                key={`connection-${idx}`}
                canDelete={false}
                connection={conn as ConnectionModel}
                withAudiences={false}
                lists={lists}
                audiences={audiences}
                update={props => updateOrEdit(idx, props)}
                activeFbPageId={activeFbPageId}
                availableTags={availableTags}
              />
            ))}
          </div>
        )}
        <div className={styles.tagsNote}>
          All tags will be added to a tag group “{companyName}”
        </div>
      </div>
    </ContactsSync>
  );
};
